import { getRegistryDocument } from '@/api/backend/registry';
import { PermitType, RegistryDocumentType } from '@/api/backend/types';
import { DownloadFilled } from '@/assets/icons';
import { FormErrorAlert, FormErrorAlertProps } from '@/components/form-error-alert';
import { ResponsiveDataView } from '@/components/responsive-data-view';
import { downloadFile } from '@/lib/utils';
import {
  DataTableColumnType,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@opaz/ui-components';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const PermitDocumentsTab = ({ data }: { data: PermitType }) => {
  const { t } = useTranslation();
  const [error, setError] = useState<FormErrorAlertProps['error']>(null);

  const { mutateAsync: downloadDocument, isPending } = useMutation({
    mutationFn: async (document: RegistryDocumentType) => {
      console.debug(`Downloading document with ID: ${document.documentNumber}`);
      const blob = await getRegistryDocument(data.registryNumber, document.documentNumber);
      return {
        blob,
        documentName: document.name,
      };
    },
    onSuccess: ({ blob, documentName }) => {
      downloadFile(blob, documentName);
    },
    onError: (error: AxiosError) => {
      console.error('Error downloading document:', error);
      setError(error);
    },
  });

  const columns: DataTableColumnType<RegistryDocumentType>[] = [
    {
      title: t('registry.permit.sectionTabs.documentsTab.table.headings.documentName'),
      render: (document) => document.name,
    },
    {
      title: t('common.download'),
      render: (document) => (
        <Link disabled={isPending} className="flex gap-2 items-center" onClick={() => downloadDocument(document)}>
          <DownloadFilled /> {t('common.download')}
        </Link>
      ),
    },
  ];

  return (
    <>
      <FormErrorAlert error={error} />
      <ResponsiveDataView
        columns={columns}
        data={data.documents ?? []}
        emptyMessage={t('registry.permit.sectionTabs.documentsTab.table.emptyState', 'No documents found')}
        getRowKey={(document) => document.documentNumber}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">
                {t('registry.permit.sectionTabs.documentsTab.table.headings.documentName')}
              </TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.documents && data.documents.length > 0 ? (
              data.documents.map((document) => (
                <TableRow key={document.documentNumber}>
                  <TableCell>{document.name}</TableCell>
                  <TableCell>
                    <Link
                      disabled={isPending}
                      className="flex gap-2 items-center"
                      onClick={() => downloadDocument(document)}
                    >
                      <DownloadFilled /> {t('common.download')}
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} className="text-center">
                  {t('registry.permit.sectionTabs.documentsTab.table.emptyState', 'No documents found')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveDataView>
    </>
  );
};
